import { useCallback } from 'react';
import { useStorageState, UseStorageStateProps } from '@hooks-clean/use-storage-state';
import { isValidObjectId } from '@utils-clean/validation';

type TimelinePanelSelection = {
    orgRef: string;
    studyRef: string;
    datasetId: number;
    selectedPanelIds: string[];
};

export const TimelinePanelsSelectedStorage: UseStorageStateProps<TimelinePanelSelection | null> = {
    key: 'SM:SubjectProfile.TimelinePanelSelection',
    defaultValue: null,
    parseFn(value: unknown) {
        if (value == null) {
            return null;
        }

        if (!(value instanceof Object)) {
            throw Error('Value missing or malformed');
        }

        // Check `orgRef` exists and is ObjectId
        if (!('orgRef' in value) || typeof value.orgRef !== 'string' || !isValidObjectId(value.orgRef)) {
            throw Error('`orgRef` missing or malformed');
        }

        // Check `studyRef` exists and is ObjectId
        if (!('studyRef' in value) || typeof value.studyRef !== 'string' || !isValidObjectId(value.studyRef)) {
            throw Error('`studyRef` missing or malformed');
        }

        // Check `datasetId` exists and is number
        if (!('datasetId' in value) || typeof value.datasetId !== 'number') {
            throw Error('`datasetId` missing or malformed');
        }

        // Check `selectedPanelIds` exists, is an array, of ObjectIds
        if (
            !('selectedPanelIds' in value) ||
            !Array.isArray(value.selectedPanelIds) ||
            value.selectedPanelIds.some((x) => typeof x !== 'string' || !isValidObjectId(x))
        ) {
            throw Error('`selectedPanelIds` missing or malformed');
        }

        return value as TimelinePanelSelection;
    },
    storageApi: sessionStorage,
    equalityComparer(a, b) {
        // If one is null, check if they're both null
        if (a == null || b == null) {
            return a === b;
        }

        // Otherwise, check each property
        if (a.orgRef !== b.orgRef) {
            return false;
        }
        if (a.studyRef !== b.studyRef) {
            return false;
        }
        if (a.datasetId !== b.datasetId) {
            return false;
        }

        if (a.selectedPanelIds.length !== b.selectedPanelIds.length) {
            return false;
        }

        for (let i = 0; i < a.selectedPanelIds.length; i++) {
            if (a.selectedPanelIds[i] !== b.selectedPanelIds[i]) {
                return false;
            }
        }

        return true;
    },
};

type Props = {
    orgRef: string;
    studyRef: string;
    datasetId: number;
};

/**
 * Persists ordered panel id list for an org/study/dataset combo. Value is persisted between unmounting and page loads.
 *
 * Value is discarded when `orgRef/`studyRef`/`datasetId` changes.
 */
export function usePersistentPanelSelection({ orgRef, studyRef, datasetId }: Props) {
    // Panels to show set by user on reorder dialog (or undefined, if dialog never used)
    const [state, setState, deleteState] = useStorageState(TimelinePanelsSelectedStorage);

    const setSelectedPanelIds = useCallback(
        (selectedPanelIds: string[]) => {
            setState({
                orgRef,
                studyRef,
                datasetId,
                selectedPanelIds,
            });
        },
        [setState, orgRef, studyRef, datasetId],
    );

    // Determine whether state is for the current org/study/dataset combo
    const stateForDifferentOrgStudyDataset =
        state != null && (state.orgRef !== orgRef || state.studyRef !== studyRef || state.datasetId !== datasetId);

    // If state is for a different org/study/dataset combo, delete it.
    if (stateForDifferentOrgStudyDataset) {
        deleteState();
    }

    // Prevent state being returned for the wrong org/study/dataset combo
    const selectedPanelIds = !stateForDifferentOrgStudyDataset ? state?.selectedPanelIds : null;

    return [selectedPanelIds, setSelectedPanelIds, deleteState] as const;
}
